// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData } from '../types';
import { ols } from '../data';
import { stripes, lineChart } from '../charts';
import { anomalyLegend } from '../color';
import { decadeMeans } from '../analysis';
import { fmtSigned, fmtRate, fmtNum, esc } from '../format';
import { gloss } from '../glossary';
import { openDrawer } from '../drawer';
import { legendHtml, statTile, sectionHead } from './ui';

export function mount(el: HTMLElement, data: ClimateData): void {
  const { meta, national, stations } = data;
  const stripeData = national.annual.map((r) => ({ y: r.y, a: r.aMean }));
  const fit = ols(national.annual.filter((r) => r.aMean != null).map((r) => [r.y, r.aMean as number]));
  const anomPts = national.annual.filter((r) => r.aMean != null).map((r) => ({ x: r.y, y: r.aMean as number }));
  const trendPts = fit ? anomPts.map((p) => ({ x: p.x, y: fit.slope * p.x + fit.intercept })) : [];
  const ymin = Math.min(...anomPts.map((p) => p.y)) - 0.1;
  const ymax = Math.max(...anomPts.map((p) => p.y)) + 0.1;

  const h35 = decadeMeans(national.annual as never, 'h35');
  const frost = decadeMeans(national.annual as never, 'frost');
  const warming = stations.filter((s) => s.rateMean != null && s.rateMean > 0).length;
  const rated = stations.filter((s) => s.rateMean != null).length;
  const top = stations.filter((s) => s.rateMean != null).slice().sort((a, b) => (b.rateMean as number) - (a.rateMean as number)).slice(0, 8);

  el.innerHTML = `
    <section class="hero">
      <div class="hero-num">${fmtSigned(meta.nationalWarming)}</div>
      <div class="hero-txt">
        <p class="hero-lead">Australia's average temperature has risen about <strong>${meta.nationalWarming.toFixed(2)} °C</strong> since ${meta.firstYear}.</p>
        <p class="hero-sub">Measured across all ${meta.stationCount} of the Bureau of Meteorology's ${gloss('acorn', 'ACORN-SAT')} ${gloss('station', 'reference stations')} — the official homogenised record. Data through ${meta.dataThrough}.</p>
      </div>
    </section>

    <section class="panel">
      ${sectionHead('A century of warming, one stripe per year', `Each band is a year, coloured by its ${'mean-temperature'} ${gloss('anomaly', 'anomaly')} against the ${gloss('baseline', '1961–1990 average')}. Blue = cooler, red = warmer. The record goes from blue to red.`)}
      <div class="stripes-wrap">${stripes(stripeData, { w: 1000, h: 150 })}</div>
      <div class="stripe-axis"><span>${national.y0}</span><span>${meta.hottestYear} was the hottest year →</span></div>
      ${legendHtml('Cooler', 'Warmer', anomalyLegend(1.6))}
    </section>

    <div class="tiles">
      ${statTile(fmtRate(meta.nationalRate), 'Warming rate', `about ${(meta.nationalRate * 10).toFixed(1)} °C per century`)}
      ${statTile(String(meta.hottestYear), 'Hottest year', `${fmtSigned(meta.hottestYearAnom)} above normal`)}
      ${statTile(fmtRate(meta.nationalRateMin), 'Nights', `warming faster than days (${fmtRate(meta.nationalRateMax)})`)}
      ${statTile(h35.late != null && h35.early != null ? `${fmtNum(h35.late)} / yr` : '—', 'Days above 35 °C', h35.late != null && h35.early != null ? `up from ${fmtNum(h35.early)} a century ago` : '')}
      ${statTile(frost.late != null && frost.early != null ? `${fmtNum(frost.late)} / yr` : '—', 'Frosty nights', frost.late != null && frost.early != null ? `down from ${fmtNum(frost.early)}` : '')}
      ${statTile(`${warming} / ${rated}`, 'Stations warming', 'nearly every station has warmed')}
    </div>

    <section class="panel">
      ${sectionHead('Australia\'s temperature, year by year', `Yearly ${gloss('mean', 'mean-temperature')} anomaly with the long-term ${gloss('trend', 'trend line')}. Hover any year for the exact figure.`)}
      <div class="chart">${lineChart([
        { name: 'Annual anomaly', color: '#b52a1f', points: anomPts, width: 1.6 },
        { name: 'Trend', color: '#1f2937', points: trendPts, width: 2, dash: true },
      ], { w: 1000, h: 320, yLabel: '°C vs 1961–1990', zeroLine: true, yMin: ymin, yMax: ymax, hoverYears: true, tipFor: (yr) => {
        const r = national.annual.find((x) => x.y === yr);
        return r && r.aMean != null ? `${yr}: ${fmtSigned(r.aMean)} · ${r.n} stations` : `${yr}: no data`;
      } })}</div>
      <div class="legend-row"><span class="lg-line" style="background:#b52a1f"></span>Annual anomaly<span class="lg-line dash" style="background:#1f2937"></span>Trend</div>
    </section>

    <section class="panel">
      ${sectionHead('Warming fastest', 'The eight reference stations warming most quickly. Click any to open its record.')}
      <div class="lead-list" data-leaders>
        ${top.map((s, i) => `
          <button class="lead-row" data-station="${s.id}">
            <span class="lead-rank">${i + 1}</span>
            <span class="lead-name">${esc(s.name)}<span class="lead-state">${s.state}</span></span>
            <span class="lead-bar-wrap"><span class="lead-bar" style="width:${((s.rateMean as number) / (top[0].rateMean as number)) * 100}%;"></span></span>
            <span class="lead-val">${fmtRate(s.rateMean)}</span>
          </button>`).join('')}
      </div>
      <p class="see-all">See every station in <a href="#view=rankings">Rankings</a> and <a href="#view=explorer">Explorer</a>.</p>
    </section>`;

  el.querySelector('[data-leaders]')?.addEventListener('click', (e) => {
    const b = (e.target as Element).closest('[data-station]') as HTMLElement | null;
    if (b) openDrawer(data, b.getAttribute('data-station')!);
  });
}
